import React, { useContext, useRef, useState, type JSX } from 'react';
import { useGlobalStore } from '../stores/global';
import { useStoryblokEditorContext } from '../components/unlisted/StoryblokEditor';
import { getLocalStorageItem, setLocalStorageItem } from '../util/storyblok/localstorage';
import { useRouter } from 'next/router';

const initialCountryContextState = {
  countryCode: '',
  setCountryCode: () => {
    throw new Error('You forgot to wrap this in a CountryContextProvider object');
  },
  countryReady: false,
  fetchCountry: () => {
    throw new Error('You forgot to wrap this in a CountryContextProvider object');
  },
};

interface ContextShape {
  countryCode: string | null;
  setCountryCode: React.Dispatch<React.SetStateAction<string | null>>;
  countryReady: boolean;
  fetchCountry: () => Promise<void>;
}

const CountryContext = React.createContext<ContextShape>(initialCountryContextState);

export const CountryContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [countryCode, setCountryCode] = useState(
    typeof window !== 'undefined'
      ? getLocalStorageItem('country')
      : initialCountryContextState.countryCode,
  );
  const [countryReady, setCountryReady] = useState(initialCountryContextState.countryReady);

  const hasBeenCalled = useRef(false);

  const store = useGlobalStore();
  const { isInEditorMode } = useStoryblokEditorContext();

  const { asPath } = useRouter();

  const fetchCountry = async () => {
    if (
      isInEditorMode ||
      hasBeenCalled.current ||
      process.env.NODE_ENV === 'development' ||
      asPath.includes(process.env.STORYBLOK_PREVIEW_SECRET!)
    )
      return;

    if (countryCode) {
      setCountryCode(countryCode);
      store.setCountryCode(countryCode);
      store.setAllCases();
      return;
    }

    try {
      hasBeenCalled.current = true;
      const response = await fetch(`https://ipwhois.pro/?key=${process.env.IPWHOIS_API_KEY}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.message);

      setLocalStorageItem('country', data.country_code);
      setCountryCode(data.country_code);
      store.setCountryCode(data.country_code);
      store.setAllCases();
    } catch (error) {
      console.error('Error fetching country data:', error);
    } finally {
      setCountryReady(true);
    }
  };

  return (
    <CountryContext.Provider
      value={{
        countryCode,
        setCountryCode,
        countryReady,
        fetchCountry,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
};

export const useCountryContext = (): ContextShape => useContext(CountryContext);
